import React, { createContext, ReactNode, useContext } from 'react';
import { Services, ServicesContainer } from './types';
import { servicesContainer } from './services-container';

/**
 * React Context for dependency injection
 * Provides services to the entire component tree
 */
const ServicesContext = createContext<Services | null>(null);

/**
 * Props for ServicesProvider
 */
interface ServicesProviderProps {
  children: ReactNode;
  container?: ServicesContainer; // Optional for testing
}

/**
 * Services Provider Component
 * Wraps the app to provide DI services via React Context
 */
export function ServicesProvider({ children, container = servicesContainer }: ServicesProviderProps) {
  const services = container.getServices();
  
  return (
    <ServicesContext.Provider value={services}>
      {children}
    </ServicesContext.Provider>
  );
}

/**
 * Hook to consume services from DI container
 * This is the main way components and hooks will access services
 */
export function useServices(): Services {
  const services = useContext(ServicesContext);
  
  if (!services) {
    throw new Error(
      'useServices must be used within a ServicesProvider. ' +
      'Make sure your app is wrapped with <ServicesProvider>.'
    );
  }
  
  return services;
}

/**
 * Higher-order component for injecting services
 * Alternative to useServices hook for class components
 */
export function withServices<P extends object>(
  Component: React.ComponentType<P & { services: Services }>
): React.ComponentType<P> {
  return function WithServicesComponent(props: P) {
    const services = useServices();
    return <Component {...props} services={services} />;
  };
}